import { app } from './LoginService';
import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import 'firebase/compat/auth';
import { Song, SongId } from '../models/Song';
import { isStillValid, needsAuthorizedUrl } from '../util/mediaRef';

export type MediaVariant = 'playback' | 'original';

interface MediaUrlResponse {
  url: string;
  kind: 'signed' | 'token' | 'external';
  /** epoch ms; null for URLs that don't expire */
  expiresAt: number | null;
}

/**
 * Resolves a song's recording to a URL the browser may load (S01).
 *
 * Non-public recordings have no download token; the getMediaUrl Cloud
 * Function checks the caller against the song's privacy tier (same contract
 * as firestore.rules canReadSong) and returns a signed URL valid for at most
 * 15 minutes. Signed URLs are cached per song until shortly before expiry.
 */
export class MediaUrlService {
  private static cache = new Map<string, MediaUrlResponse>();

  // Keyed by viewer too: authorization differs per signed-in user.
  private static key = (songId: SongId, variant: MediaVariant) =>
    `${firebase.auth().currentUser?.uid || 'guest'}:${variant}:${songId}`;

  /** Synchronous: a URL usable right now without a network round trip, if any. */
  static peek = (song: Song, variant: MediaVariant = 'playback'): string | undefined => {
    const src = variant === 'original' ? song.media?.originalSrc || song.media?.src : song.media?.src;
    if (src && !needsAuthorizedUrl(src)) return src;
    const hit = MediaUrlService.cache.get(MediaUrlService.key(song.id, variant));
    return hit && isStillValid(hit.expiresAt) ? hit.url : undefined;
  }

  /**
   * @param force skip the stored src / cache and ask the server (used when
   *   the media element reports an error, e.g. 403 after a token was revoked
   *   or a signed URL expired)
   */
  static resolve = async (song: Song, variant: MediaVariant = 'playback', force = false): Promise<string> => {
    if (!force) {
      const quick = MediaUrlService.peek(song, variant);
      if (quick) return quick;
    }
    const callable = firebase.functions(app).httpsCallable('getMediaUrl');
    const result = await callable({ songId: song.id, variant });
    const data = result.data as MediaUrlResponse;
    MediaUrlService.cache.set(MediaUrlService.key(song.id, variant), data);
    return data.url;
  }

  /** Forget cached URLs, e.g. on sign-out (authorization may change). */
  static clear = () => {
    MediaUrlService.cache.clear();
  }
}
